import React, { useMemo, useState } from "react";
import {
  Accordion,
  Alert,
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Modal,
  Row,
  Spinner,
} from "react-bootstrap";
import SuccessButton from "../../organisms/SuccessButton";
import ServerValidationErrorAlert from "../../organisms/ServerValidationErrorAlert";
import { ValidationErrorResponse } from "../../../services/ValidationError";
import { GrupoUsuario, NuevoGrupoUsuario, RolSeguridad } from "../../../services/seguridadServicios";
import GrupoUsuarioAccordion from "./GrupoUsuarioAccordion";

const CODIGO_GRUPO_REGEX = /^[\p{L}\p{N}_]+$/u;

const validarCodigoGrupo = (codigo: string) => {
  if (!codigo) {
    return "El código del grupo es obligatorio.";
  }

  if (!CODIGO_GRUPO_REGEX.test(codigo)) {
    return "El código solo puede contener letras, números o guiones bajos (_).";
  }

  return undefined;
};

interface ActionResult {
  validationErrors?: ValidationErrorResponse;
}

interface GruposUsuariosSectionProps {
  grupos: GrupoUsuario[];
  roles: RolSeguridad[];
  estaCreandoGrupo: boolean;
  idGrupoGuardando?: string;
  idGrupoEliminando?: string;
  onCrearGrupo: (body: NuevoGrupoUsuario) => Promise<ActionResult>;
  onGuardarGrupo: (payload: {
    idGrupoUsuario: string;
    body: NuevoGrupoUsuario;
  }) => Promise<ActionResult>;
  onEliminarGrupo: (idGrupoUsuario: string) => Promise<void>;
}

function GruposUsuariosSection({
  grupos,
  roles,
  estaCreandoGrupo,
  idGrupoGuardando,
  idGrupoEliminando,
  onCrearGrupo,
  onGuardarGrupo,
  onEliminarGrupo,
}: GruposUsuariosSectionProps) {
  const [mostrarModalCrear, setMostrarModalCrear] = useState(false);
  const [nuevoCodigo, setNuevoCodigo] = useState("");
  const [errorCodigoNuevo, setErrorCodigoNuevo] = useState<string | undefined>();
  const [serverFailedValidations, setServerFailedValidations] = useState<
    ValidationErrorResponse | undefined
  >(undefined);

  const codigosExistentes = useMemo(() => {
    return new Set(grupos.map((grupo) => grupo.codigo));
  }, [grupos]);

  const codigoGrupoEsUnico = (grupoId: string, codigo: string) =>
    !grupos.some((g) => g.id !== grupoId && g.codigo === codigo);

  const onCrearGrupoClick = async () => {
    setServerFailedValidations(undefined);
    const errorValidacion = validarCodigoGrupo(nuevoCodigo);
    if (errorValidacion) {
      setErrorCodigoNuevo(errorValidacion);
      return;
    }

    if (codigosExistentes.has(nuevoCodigo)) {
      setErrorCodigoNuevo("Ya existe un grupo con ese código.");
      return;
    }

    setErrorCodigoNuevo(undefined);
    const result = await onCrearGrupo({
      codigo: nuevoCodigo,
      roles: [],
    });

    if (result.validationErrors) {
      setServerFailedValidations(result.validationErrors);
      return;
    }

    setMostrarModalCrear(false);
    setNuevoCodigo("");
    setErrorCodigoNuevo(undefined);
    setServerFailedValidations(undefined);
  };

  return (
    <>
      <Row className="justify-content-center">
        <div className="col-xs-12 col-md-12 col-lg-11 col-xxl-8">
          <Card>
            <Card.Body>
              <Row className="justify-content-between align-items-center mb-3">
                <Col>
                  <h5 className="mb-0">Grupos de usuarios</h5>
                </Col>
                <Col xs="auto">
                  <Button onClick={() => setMostrarModalCrear(true)}>Crear</Button>
                </Col>
              </Row>

              {grupos.length === 0 && (
                <Alert variant="info" className="mb-0">
                  Aún no hay grupos de usuarios.
                </Alert>
              )}

              {grupos.length > 0 && (
                <Accordion>
                  {grupos.map((grupo) => (
                    <GrupoUsuarioAccordion
                      key={grupo.id}
                      grupo={grupo}
                      roles={roles}
                      codigoGrupoEsUnico={codigoGrupoEsUnico}
                      estaGuardando={idGrupoGuardando === grupo.id}
                      estaEliminando={idGrupoEliminando === grupo.id}
                      onGuardarGrupo={onGuardarGrupo}
                      onEliminarGrupo={onEliminarGrupo}
                    />
                  ))}
                </Accordion>
              )}
            </Card.Body>
          </Card>
        </div>
      </Row>

      <Modal
        centered
        show={mostrarModalCrear}
        onHide={() => {
          if (!estaCreandoGrupo) {
            setMostrarModalCrear(false);
            setErrorCodigoNuevo(undefined);
            setServerFailedValidations(undefined);
          }
        }}
      >
        <Modal.Header closeButton={!estaCreandoGrupo}>
          <Modal.Title>Crear Grupo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {serverFailedValidations && (
            <ServerValidationErrorAlert
              serverFailedValidations={serverFailedValidations}
              wrapperClass="mb-3"
            />
          )}
          <InputGroup>
            <InputGroup.Text>Código</InputGroup.Text>
            <Form.Control
              value={nuevoCodigo}
              disabled={estaCreandoGrupo}
              isInvalid={!!errorCodigoNuevo}
              onChange={(event) => {
                setNuevoCodigo(event.target.value);
                setErrorCodigoNuevo(undefined);
                setServerFailedValidations(undefined);
              }}
            />
            <Form.Control.Feedback type="invalid">
              {errorCodigoNuevo}
            </Form.Control.Feedback>
          </InputGroup>
        </Modal.Body>
        <Modal.Footer>
          {estaCreandoGrupo ? (
            <Spinner
              animation="border"
              size="sm"
              role="status"
              aria-label="Creando grupo"
            />
          ) : (
            <SuccessButton onClick={onCrearGrupoClick}>Crear</SuccessButton>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default GruposUsuariosSection;
